import React, { useCallback } from 'react';

import { CircularProgress } from '@mui/material';
import { MAIN_THEME } from '@styles';
import { isEmpty, isValidNumber } from '@utils/validationUtils';

import FormFieldWrapper from './FormFieldWrapper';
import { IFormFieldInputBaseProps } from '.';

import clsx from 'clsx';
import { makeStyles } from 'tss-react/mui';

export interface IPercentageFormFieldProps extends IFormFieldInputBaseProps {
  value: string | number;
  disableNegativeValues?: boolean;
  minValue?: number;
  maxValue?: number;
  maxDecimalPrecision?: number;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
  hidden?: boolean;
  classes?: {
    inlineContainer?: string;
    inputContainer?: string;
    input?: string;
    percentageSymbol?: string;
  };
}

const useStyles = makeStyles()(() => ({
  inlineContainer: {
    position: 'relative',
    fontFamily: MAIN_THEME.font.regular
  },
  inputContainer: {
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    margin: '0 auto',
    height: 35,
    width: '100%',
    fontFamily: MAIN_THEME.font.regular,
    backgroundColor: MAIN_THEME.color.white98,
    border: `1px solid ${MAIN_THEME.color.white86}`,
    borderRadius: '5px'
  },
  input: {
    fontFamily: MAIN_THEME.font.regular,
    fontSize: MAIN_THEME.fontSize.sm,
    lineHeight: '15px',
    height: 35,
    width: '100%',
    padding: '0 15px',
    margin: '0',
    backgroundColor: 'transparent',
    border: 'none',
    borderRadius: '5px',
    MozAppearance: 'textfield',
    '&:disabled': {
      cursor: 'not-allowed',
      color: 'rgba(0, 0, 0, 0.38)'
    },
    '&::placeholder': {
      fontStyle: 'italic',
      fontFamily: MAIN_THEME.font.regular,
      color: MAIN_THEME.color.white86,
      opacity: 1,
      fontSize: MAIN_THEME.fontSize.sm
    },
    '&:focus': {
      outline: 'none'
    },
    '::-webkit-inner-spin-button': {
      WebkitAppearance: 'none',
      margin: 0
    }
  },
  materialContainer: {
    backgroundColor: 'unset',
    border: 'none',
    borderBottom: `1px solid ${MAIN_THEME.color.white86}`,
    color: MAIN_THEME.color.black,
    margin: '0 auto',
    borderRadius: 0,
    padding: '10px 8px 4px 4px',
    fontFamily: MAIN_THEME.font.regular,
    fontSize: `${MAIN_THEME.fontSize.sm}`
  },
  materialInput: {
    backgroundColor: 'unset',
    border: 'none',
    color: MAIN_THEME.color.black,
    borderRadius: 0,
    padding: '10px 15px 11px 0px',
    fontFamily: MAIN_THEME.font.regular,
    fontSize: `${MAIN_THEME.fontSize.sm}`
  },
  inputError: {
    borderColor: `${MAIN_THEME.color.red} !important`
  },
  inputDisabled: {
    cursor: 'not-allowed',
    color: 'rgba(0, 0, 0, 0.38)'
  },
  percentageSymbol: {
    fontSize: MAIN_THEME.fontSize.sm,
    fontFamily: MAIN_THEME.font.regular,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 'fit-content',
    padding: '0px',
    paddingRight: '0.8em'
  },
  materialPercentageSymbol: {
    padding: '0px !important'
  },
  disabledPercentageSymbol: {
    color: 'rgba(0, 0, 0, 0.38)!important'
  },
  loader: {
    position: 'absolute',
    top: 'calc(50% - 12px)',
    right: '6%',
    opacity: 1,
    height: '25px',
    width: '25px'
  }
}));

const PercentageFormField: React.FC<IPercentageFormFieldProps> = ({
  name,
  title,
  value,
  placeholder,
  onChange,
  onBlur,
  material,
  disableNegativeValues,
  error,
  loader,
  inputOnly,
  disabled,
  onFocus,
  hideError,
  minValue = 0,
  maxValue = 100,
  maxDecimalPrecision = 3,
  classes: fieldClasses = {}
}) => {
  const { classes: inputClasses } = useStyles();

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      let newValue = event.target.value;

      if (newValue === '') {
        onChange(event);

        return;
      }

      if (Number(newValue) < 0 && disableNegativeValues) {
        newValue = minValue.toString();
      }

      const pattern = /^-?\d*\.?\d*$/;
      if (!pattern.test(newValue)) return;

      if (newValue.includes('.') && maxDecimalPrecision >= 0) {
        const [intPart, fracPart] = newValue.split('.');
        if (fracPart.length > maxDecimalPrecision) {
          newValue = `${intPart}.${fracPart.slice(0, maxDecimalPrecision)}`;
        }
      }

      const floatValue = parseFloat(newValue);
      if (isNaN(floatValue)) return;

      if (
        (isValidNumber(minValue) && floatValue < minValue) ||
        (isValidNumber(maxValue) && floatValue > maxValue)
      ) {
        return;
      }

      event.target.value = newValue;
      onChange(event);
    },
    [onChange, minValue, maxValue, disableNegativeValues, maxDecimalPrecision]
  );

  const handleBlur = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      const value = event.target.value;
      if (!isEmpty(value) && maxDecimalPrecision >= 0) {
        const floatValue = parseFloat(value);
        if (!isNaN(floatValue)) {
          event.target.value = floatValue
            .toFixed(maxDecimalPrecision)
            .replace(/\.?0+$/, '');
          onChange(event as any);
        }
      }
      onBlur?.(event);
    },
    [onChange, onBlur, maxDecimalPrecision]
  );

  const renderInput = () => (
    <div
      className={clsx(inputClasses.inputContainer, fieldClasses.inputContainer, {
        [inputClasses.inputError]: error,
        [inputClasses.inputDisabled]: disabled,
        [inputClasses.materialContainer]: material
      })}
    >
      <input
        className={clsx(inputClasses.input, fieldClasses.input, {
          [inputClasses.materialInput]: material
        })}
        title={disabled && !isEmpty(value) ? value?.toString() : title}
        id={name}
        name={name}
        aria-invalid={!!error}
        aria-describedby={`errorMsg-${name}`}
        value={value === null || value === undefined ? '' : String(value)}
        onChange={handleChange}
        onBlur={handleBlur}
        onFocus={onFocus}
        type="text"
        disabled={disabled}
        placeholder={placeholder}
        onWheel={(e) => (e.target as any).blur()}
        min={minValue}
        max={maxValue}
      />
      <div
        className={clsx(
          inputClasses.percentageSymbol,
          fieldClasses.percentageSymbol,
          {
            [inputClasses.materialPercentageSymbol]: material,
            [inputClasses.disabledPercentageSymbol]: disabled
          }
        )}
      >
        %
      </div>
    </div>
  );

  return inputOnly ? (
    <span className={clsx(inputClasses.inlineContainer, fieldClasses.inlineContainer)}>
      {renderInput()}
    </span>
  ) : (
    <FormFieldWrapper
      title={title}
      error={error}
      name={name}
      material={material}
      hideError={hideError}
    >
      {renderInput()}
      {loader && (
        <CircularProgress
          className={inputClasses.loader}
          size={20}
          thickness={3}
        />
      )}
    </FormFieldWrapper>
  );
};

export default PercentageFormField;
